import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Modal, Textinput, Typo, Flex, SimpleButton, theme } from '../../../ui'; // Added Alert
import { generateGiftCodes, selectStatus, selectGiftCodeError } from '../../invites/invitesSlice'; // Added selectors
import { CSVLink } from '../../createCsv';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CheckboxButton from '../../../ui/CheckboxButton';
import { selectFeatures } from '../../settings/settingsSlice';

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const GenerateGiftCodesModal = ({ course, onClose, isAdmin }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const features = useSelector(selectFeatures);
  const status = useSelector(selectStatus); // Get status from store
  const giftCodeError = useSelector(selectGiftCodeError); // Get error from store

  const amountRef = useRef(null);
  const idRef = useRef(null);
  const yearRef = useRef(null);
  
  const [exportData, setExportData] = useState(null);
  const [isCoursePackage, setIsCoursePackage] = useState(false);

  // Clear export data and error when modal is opened/closed or course changes
  useEffect(() => {
    setExportData(null);
  }, [course, onClose]);

  const handleCreateGiftCodes = useCallback(async () => {
    setExportData(null); // Clear previous export data
    const payload = {
      amount: amountRef.current.value,
      objectType: isCoursePackage ? 'package' : 'course',
      courseId: idRef?.current?.value || course?.id,
    };
    if (yearRef.current.value) {
      payload.year = parseInt(yearRef.current.value, 10); // Ensure year is an integer
    }
    
    dispatch(generateGiftCodes(payload))
      .unwrap()
      .then(result => {
        const codes = result.codes || result ;
        const data = codes.map(item => ({
          codes: item,
          expire: result.expiry,
        }));
        setExportData(data);
      });

  }, [dispatch, course, setExportData, isCoursePackage, idRef, amountRef, yearRef]);

  const toggleCoursePackage =(e)=>{
    setIsCoursePackage(e.target.checked)
  }

  return (
    <Modal
      open={!!course}
      onClose={onClose}
      title={course?.name || t('course.typeYourCourse')}
      width='650px'
    >
      <Flex direction='column' gap='10' width='100%'>
        {isAdmin && !course?.id && <Flex direction='column'width='100%' align='flex-start'>
          <Typo.Text>course id</Typo.Text>
          <Textinput
            type='number'
            inputRef={idRef}
            margin='dense'
            id='courseId'
            fullWidth
          />
          <CheckboxButton
            labelText={t('course.courseIsPackage')}
            checked={isCoursePackage}
            handleChange={toggleCoursePackage}
          />
        </Flex>}
        <Typo.Text>{t('course.howManyCodes')}</Typo.Text>
        <Textinput
          autoFocus
          type='number'
          inputRef={amountRef}
          margin='dense'
          id='giftCodes'
          fullWidth
          disabled={status === 'loading'}
        />
        { (features?.giftCodeExpiration || isAdmin) && <>
        <Typo.Text>{t('course.validationTimeSpanInYears')}</Typo.Text> 
        <Textinput
          type='number'
          inputRef={yearRef}
          margin='dense'
          id='validationYears'
          fullWidth
          disabled={status === 'loading'}
        />
        </>}
      </Flex>

      <SimpleButton 
        startIcon='Sync' 
        onClick={handleCreateGiftCodes}
        loadingText={t('common.loading')}
        isLoading={status === 'loading'}
      >
        { t('course.createGiftCodes')}
      </SimpleButton>
      <Controls>
        {giftCodeError && (
          <Typo.SmallText color={theme.colors.ERROR_RED} margin='10px 0'>
            {giftCodeError}
          </Typo.SmallText>
        )}
        {exportData?.length && !giftCodeError && (
          <CSVLink
            data={exportData}
            filename={`${t('course.giftCodes')} ${course?.name || idRef?.current?.value} ${dayjs().format('YYYY-MM-DD')}.csv`}
          >
            {t('course.downloadFile')}
          </CSVLink>
        )}
      </Controls>

    </Modal>
  );
};

export default GenerateGiftCodesModal;
